// filepath: apps/web/src/features/tasks/types.ts
//
// Wire types for the human-task inbox API surface (FR-HT-1..5).
//
// These mirror the Go structs in internal/task/handlers.go and are
// kept in sync with the API contract documented in the impact analysis.
//
// SECURITY: field-level RBAC stripping is done server-side before the
// response is serialized (INV-2 / FR-AUTH-7). The SPA renders what it
// receives — no client-side visibility filtering.

import type { DefinitionKey } from "@/definitions/types";

/**
 * The shape of a single task returned by GET /api/tasks/inbox.
 *
 * `form_definition` carries the pinned version (FR-BPM-10 / FR-UI-CORE-3).
 * The SPA MUST fetch exactly this version, never 'active'.
 */
export interface HumanTask {
  id: string;
  /** The BPM process instance this task belongs to. */
  instance_id: string;
  /** The human-readable task title (from the process definition). */
  title: string;
  /** The process definition name. */
  process_name: string;
  /** The form definition id for this task's completion interaction. */
  form_id: string;
  /**
   * The pinned form version (FR-BPM-10). Always a positive integer.
   * AC-4 (FR-UI-TASK-1): MUST be used as `version` in the
   * `DefinitionKey` when fetching the form — never substitute 'active'.
   */
  form_version: number;
  /**
   * The dynamic-entity row id that is the subject of this task.
   * Used as `entityId` when calling the complete endpoint.
   */
  record_id: string;
  /**
   * The row_version of the record at the time the task was created / claimed.
   * Sent on completion for the server's optimistic-concurrency check (FR-ENT-8).
   */
  row_version: number;
  /** Null when the task is unclaimed. */
  assignee_user_id: string | null;
  /** The candidate role that can claim this task. */
  candidate_role_id: string | null;
  /** Human-task status: 'unclaimed' | 'claimed' | 'completed' | 'cancelled'. */
  status: HumanTaskStatus;
  /**
   * ISO-8601 timestamp of when this task's work is due.
   * Null when no due date is set.
   */
  due_at: string | null;
  /** ISO-8601 timestamp of when the task was created. */
  created_at: string;
}

export type HumanTaskStatus =
  "unclaimed" | "claimed" | "completed" | "cancelled";

/** The inbox list response shape. */
export interface InboxResponse {
  tasks: HumanTask[];
  total: number;
}

/** The query parameters for GET /api/tasks/inbox. */
export interface InboxQuery {
  /** 'mine' returns assigned tasks; 'unclaimed' returns role-visible unclaimed tasks. */
  view: "mine" | "unclaimed";
  limit?: number;
  offset?: number;
}

/** The request body for POST /api/tasks/{id}/claim. */
export type ClaimRequest = Record<string, never>;

/** The request body for POST /api/tasks/{id}/complete. */
export interface CompleteTaskRequest {
  /** The task being completed. */
  task_id: string;
  /**
   * The row_version of the record at the time of completion.
   * The server checks this against its current value (FR-ENT-8 / INV-7).
   */
  row_version: number;
  /**
   * The task outcome key (e.g. 'approve', 'reject', 'submit').
   * The shape of form_data is tenant/form-specific.
   */
  outcome?: string;
  /** The submitted form field values. */
  form_data: Record<string, unknown>;
}

/** The request body for POST /api/tasks/{id}/delegate [S]. */
export interface DelegateTaskRequest {
  /** The user id to delegate this task to. */
  delegate_to_user_id: string;
}

/** Build a DefinitionKey for the pinned form version of a task. */
export function taskFormKey(task: HumanTask): DefinitionKey {
  return {
    type: "form",
    id: task.form_id,
    version: task.form_version,
  };
}
